import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { last } from 'lodash';
import { DateTime, Duration } from 'luxon';

import SessionHelper from '../helpers/SessionHelper';
import PriceHelper from '../../shared/helpers/PriceHelper';
import Tooltip from './Tooltip';

import './SessionList.css';
import Gridrow from './Gridrow';
import { isScrolledToBottom } from '../../shared/utils/scroll';
import { assertIsNotNil } from '../../shared/utils/assert';
import { intersperseUnavailableBlocks } from '../../shared/utils/unavailableBlocks';
import { ConfigContext } from '../ConfigContext';
import { formatTimeDt, parseTimeToFddt } from '../../shared/utils/datetime';
import PropTypes from 'prop-types';
/**
 * @typedef {{
 *   tooltip: boolean;
 * }} State
 *
 * @typedef {{
 *   didUpdate: () => void;
 *   sessions: unknown[],
 *   filter: Record<string, unknown>,
 *   customStyle: string | Record<string, unknown>,
 *   checkoutUrl: string,
 *   checkoutTheme: string,
 *   paddingTop: number | string,
 *   height?: number | null,
 *   shouldShowNoAvailability: boolean,
 *   shouldShowExactAvailability: boolean,
 *   shouldNotShowDeepLinkButton: boolean,
 *   memberDeepLinks: Record<string, unknown>,
 *   bookableProviders: string[],
 *   isCategoryFilter: boolean,
 *   admissionsPolicyUrl: string,
 *   activityList: Record<string, unknown>,
 *   showSportActivityLocation: boolean
 * }} Props
 *
 * @typedef {{
 *   time: string;
 *   name: string;
 *   activityList: any[];
 *   price: any;
 *   description: string;
 *   subEvent: any;
 *   eventSeries: any;
 *   provider: string;
 *   uniqueId: string;
 *   isUnavailableBlock: boolean;
 * }} SessionGridRowItem
 */

/**
 * @param {Props} props
 */
function SessionList(props) {
  const timetableConfig = useContext(ConfigContext);
  const [state, setState] = useState(
    /** @type {State} */ ({
      tooltip: false,
    })
  );
  const [openRowId, setOpenRowId] = useState(null);
  const listScroll = useRef(false);
  /** @type {React.MutableRefObject<HTMLDivElement | null>} */
  const node = useRef(null);

  const isNodeScrolledToBottom = () => isScrolledToBottom(node.current);

  const handleScroll = () => {
    const isAtBottom = isNodeScrolledToBottom();
    if (!isAtBottom && !state.tooltip) {
      setState({ ...state, tooltip: true });
    } else if (isAtBottom && state.tooltip) {
      setState({ ...state, tooltip: false });
    }
  };

  const { scrollHandler, stopScrollHandler } = useMemo(() => {
    let timer;

    return {
      scrollHandler() {
        clearTimeout(timer);
        timer = setTimeout(() => {
          handleScroll();
        }, 100);
      },
      stopScrollHandler() {
        clearTimeout(timer);
      },
    };
  }, []);

  const attachListScroll = useMemo(
    () => () => {
      if (!listScroll.current && node.current) {
        node.current.addEventListener('scroll', scrollHandler);
        listScroll.current = true;
      }
    },
    [scrollHandler]
  );

  const sessionGridRowItems = useMemo(() => {
    return getSessionGridRowItems(
      timetableConfig,
      props.sessions || [],
      props.filter || {},
      props.isCategoryFilter,
      props.activityList
    );
  }, [props.sessions, props.filter, props.isCategoryFilter, props.activityList]);

  useEffect(() => {
    if (typeof props.didUpdate === 'function') {
      props.didUpdate();
    }
    attachListScroll();
    handleScroll();
  });

  useEffect(() => {
    window.addEventListener('scroll', scrollHandler);
    attachListScroll();
    const firstScrollHandle = setTimeout(() => {
      handleScroll();
    }, 500);

    return () => {
      clearTimeout(firstScrollHandle);
      stopScrollHandler();
      window.removeEventListener('scroll', scrollHandler);
      if (node.current) {
        node.current.removeEventListener('scroll', scrollHandler);
      }
    };
  }, []);

  useEffect(() => {
    if (sessionGridRowItems.length === 0) {
      listScroll.current = false;
    }
  }, [sessionGridRowItems]);

  if (sessionGridRowItems.length === 0) {
    return (
      <div className="NoResults">
        <span>
          <p>There are no sessions available with the current filter sessions.</p>
          <p>Try adjusting the filters to see what else is available.</p>
        </span>
      </div>
    );
  }

  const style = {
    paddingTop: props.paddingTop,
  };

  if (props.height) {
    style.maxHeight = `${props.height - 10.5}rem`;
  }

  let tooltip;
  if (state.tooltip) {
    tooltip = <Tooltip title="Scroll down for more sessions" />;
  }

  return (
    <div ref={node}>
      <div className="Grid">
        {sessionGridRowItems.map((sessionItem) => (
          <Gridrow
            key={sessionItem.uniqueId}
            {...sessionItem}
            isDescriptionOpen={openRowId === sessionItem.uniqueId}
            onToggle={() =>
              setOpenRowId((prev) => (prev === sessionItem.uniqueId ? null : sessionItem.uniqueId))
            }
            shouldShowExactAvailability={props.shouldShowExactAvailability}
            shouldShowNoAvailability={
              sessionItem.isUnavailableBlock ? true : props.shouldShowNoAvailability
            }
            memberDeepLinks={props.memberDeepLinks}
            checkoutBaseUrl={props.checkoutUrl}
            bookableProviders={props.bookableProviders}
            shouldNotShowDeepLinkButton={props.shouldNotShowDeepLinkButton}
            checkoutTheme={props.checkoutTheme}
            admissionsPolicyUrl={props.admissionsPolicyUrl}
            showActivityTypes={props.showActivityTypes}
            showPrices={props.showPrices}
            showSportActivityLocation={props.showSportActivityLocation}
            sportActivityLocationLock={props.sportActivityLocationLock}
          />
        ))}
      </div>
      {/* TODO tooltip is not included at present for some reason.

        Maybe re-introduce once
        https://trello.com/c/PF2I7vBM/4327-fu-tt-scroll-down-for-more-tooltip-doesnt-disappear-once-scrolled-down-in-embedded-mode
        has been completed.
        */}
    </div>
  );
}

/**
 * @param {import('../Config').Config} timetableConfig
 * @param {any[]} eventSeriesList
 * @param {any} filter
 * @param {boolean} isCategoryFilter
 * @param {Record<string, unknown>} activityList
 * @returns {SessionGridRowItem[]}
 */
function getSessionGridRowItems(
  timetableConfig,
  eventSeriesList,
  filter,
  isCategoryFilter,
  activityList
) {
  /**
   * @type {{
   *   startTimeFddt: DateTime;
   *   endTimeFddt: DateTime;
   *   payload: SessionGridRowItem;
   * }[]}
   */
  const sessionItems = [];

  for (const eventSeries of eventSeriesList) {
    const provider = eventSeries['imin:dataSource'].identifier;
    for (const sessionSeries of eventSeries.subEvent) {
      if (!sessionSeries.subEvent) {
        continue;
      }
      for (const subEvent of sessionSeries.subEvent || []) {
        const isInFilter = SessionHelper.isWithinFilter(
          isCategoryFilter,
          eventSeries,
          subEvent,
          filter,
          activityList
        );
        if (isInFilter) {
          const startTimeString = formatTimeDt(DateTime.fromISO(subEvent.startDate));
          let endTime = DateTime.fromISO(subEvent.endDate);
          if (endTime.second === 59) {
            endTime = endTime.plus({ seconds: 1 });
          }

          const endTimeString = formatTimeDt(endTime);
          const uniqueId = `${eventSeries.identifier}-${subEvent.startDate}-${sessionItems.length}`;
          const thisActivityList = getActivityList(eventSeries);
          const price = PriceHelper.getPricesList(
            eventSeries['imin:aggregateOffer'],
            provider,
            eventSeries.name
          );
          const timeStr = `${startTimeString} - ${endTimeString}`;

          sessionItems.push({
            startTimeFddt: parseTimeToFddt(startTimeString),
            endTimeFddt: parseTimeToFddt(endTimeString),
            payload: {
              time: timeStr,
              name: eventSeries.name,
              activityList: thisActivityList,
              price,
              description: SessionHelper.description(eventSeries.description),
              subEvent,
              eventSeries,
              provider,
              uniqueId,
              isUnavailableBlock: false,
            },
          });
        }
      }
    }
  }

  // Add automatic "unavailable" blocks in between any spaces
  const allBlocks = (() => {
    if (
      !timetableConfig.automaticUnavailableBlocks ||
      timetableConfig.automaticUnavailableBlocks.length === 0
    ) {
      return sessionItems;
    }
    return intersperseUnavailableBlocks(
      (config, start, end) => {
        const startStr = formatTimeDt(start);
        const endStr = formatTimeDt(end);
        return {
          time: `${startStr} - ${endStr}`,
          name: config.name,
          activityList: [],
          price: {},
          description: '',
          subEvent: {},
          eventSeries: {},
          provider: '',
          uniqueId: `unavailable-${startStr}`,
          isUnavailableBlock: true,
        };
      },
      timetableConfig.automaticUnavailableBlocks[0],
      sessionItems
    );
  })();
  return allBlocks.map((b) => b.payload);
}

/**
 * @returns {any[]}
 */
function getActivityList(eventSeries) {
  let activityList = [];
  if (eventSeries.activity instanceof Array && eventSeries.activity.length) {
    activityList = eventSeries.activity
      .filter((activity) => activity.prefLabel)
      .map((_) => ({ name: _.prefLabel }));
  }
  return activityList;
}

SessionList.propTypes = {
  didUpdate: PropTypes.func,
  sessions: PropTypes.array.isRequired,
  filter: PropTypes.object.isRequired,
  isCategoryFilter: PropTypes.bool.isRequired,
  activityList: PropTypes.object.isRequired,
  paddingTop: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  shouldShowNoAvailability: PropTypes.bool.isRequired,
  shouldShowExactAvailability: PropTypes.bool.isRequired,
  shouldNotShowDeepLinkButton: PropTypes.bool.isRequired,
  memberDeepLinks: PropTypes.object.isRequired,
  bookableProviders: PropTypes.array.isRequired,
  checkoutUrl: PropTypes.string.isRequired,
  checkoutTheme: PropTypes.string.isRequired,
  admissionsPolicyUrl: PropTypes.string,
  showActivityTypes: PropTypes.bool.isRequired,
  showPrices: PropTypes.bool.isRequired,
  showSportActivityLocation: PropTypes.bool.isRequired,
  sportActivityLocationLock: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
};

export default SessionList;
